import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useConversationMessageListControllerGetConversationsQuery } from '@shared/services/conversationApiService/apiService';
import { downloadDialogAsPdf } from '@features/Admin/Campaign/utils/js-pdf.util';
import { DocMessage } from '@features/Admin/Campaign/model/type';
import { useAppSelector } from '@shared/store';
import { selectors } from '@shared/store/ducks/auth';
import { AuthenticatedPath } from '@shared/constants/routes.constants';

export const ConversationDownload: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const { t } = useTranslation();

  const isAuth = useAppSelector(selectors.getAccessToken);

  const {
    data: conversationDialog,
    error,
    isLoading,
  } = useConversationMessageListControllerGetConversationsQuery(
    { conversationId: id! },
    { skip: !id || !isAuth },
  );

  if (!isAuth) {
    return <Navigate to="/sign-in" replace />;
  }

  if (!id) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Alert severity="error">{t('conversationDownload.errors.invalidConversationId')}</Alert>
      </Box>
    );
  }

  useEffect(() => {
    if (conversationDialog && !isLoading && !error && !isDownloading) {
      setIsDownloading(true);

      try {
        const recipient = `Conversation-${id}`;

        const dialogMessages: DocMessage[] = conversationDialog.map((msg: any) => ({
          id: msg.id,
          conversationId: msg.conversationId,
          actor: msg.actor,
          message: msg.message,
          createdAt: msg.createdAt,
          updatedAt: msg.updatedAt,
        }));

        downloadDialogAsPdf(recipient, dialogMessages);

        setTimeout(() => {
          window.location.href = AuthenticatedPath.CAMPAIGN;
        }, 1000);
      } catch (err) {
        console.error('Error downloading PDF:', err);
        setDownloadError(t('conversationDownload.errors.failedToDownloadPdf'));
        setIsDownloading(false);
      }
    }
  }, [conversationDialog, isLoading, error, id, isDownloading, t]);

  useEffect(() => {
    if (error) {
      console.error('Error fetching conversation:', error);
      if ('status' in error && error.status === 401) {
        setDownloadError(t('conversationDownload.errors.unauthorizedAccess'));
      } else {
        setDownloadError(t('conversationDownload.errors.failedToFetchConversation'));
      }
    }
  }, [error, t]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        gap={2}>
        <CircularProgress />
        <Typography>{t('conversationDownload.loadingConversation')}</Typography>
      </Box>
    );
  }

  if (downloadError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Alert severity="error">{downloadError}</Alert>
      </Box>
    );
  }

  if (isDownloading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        gap={2}>
        <CircularProgress />
        <Typography>{t('conversationDownload.preparingPdfDownload')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('conversationDownload.redirectingShortly')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
      <Typography>{t('conversationDownload.initiatingDownload')}</Typography>
    </Box>
  );
};
